import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppModal } from "@/shared/Components";
import { TrashBinIcon } from "@/shared/icons";
import type { FC } from "react";
import type { NotesTableItem } from "@/features/NotesManager/model";
import type { NoteTypes } from "@/services/api/entities/note/types";
import { getTypeName } from "../../helpers/get-type-name";
import { DeleteNoteItem } from "../DeleteNoteItem";
import styles from "./styles.module.scss";

type DeleteNotesModalProps = {
  open: boolean;
  notes: NotesTableItem[];
  onCancel: () => void;
  onConfirm: (deletedNotes: string[]) => void;
  noteType: NoteTypes;
};

export const DeleteNotesModal: FC<DeleteNotesModalProps> = ({
  open,
  onCancel,
  notes,
  onConfirm,
  noteType,
}) => {
  const { t } = useTranslation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    const ids = notes.map((note) => note.id);

    setSelectedIds(ids);
  }, [notes]);

  const toggleCheckbox = useCallback((id: string) => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter((selectedId) => selectedId !== id)
        : [...prevSelectedIds, id],
    );
  }, []);

  return (
    <AppModal
      open={open}
      className={styles.root}
      title={t("notesPage.deleteModal", {
        noteType: getTypeName(noteType),
      })}
      okText={t("common.delete")}
      cancelText={t("common.cancel")}
      onCancel={onCancel}
      okButtonProps={{
        className: styles["delete-button"],
        icon: <TrashBinIcon className={styles.icon} />,
      }}
      destroyOnClose={true}
      onOk={() => onConfirm(selectedIds)}
    >
      {notes.map((note) => {
        const checked = selectedIds.includes(note.id);

        return (
          <DeleteNoteItem
            note={note}
            key={note.id}
            checked={checked}
            toggleCheckbox={toggleCheckbox}
          />
        );
      })}
    </AppModal>
  );
};
